import { useState, useEffect } from 'react';
import { getCollection } from '../../services/firestoreService';
import { aggregateSubscriptions } from '../../services/subscriptionAggregator';
import { getAnalyticsEvents } from '../../services/analyticsService';
import { getActiveSessions } from '../../services/activeSessionsService';
import { getCrashAnalytics } from '../../services/crashAnalyticsService';

interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: string;
  color: string;
}

const KPICard: React.FC<KPICardProps> = ({ title, value, subtitle, icon, color }) => (
  <div style={{
    backgroundColor: 'white',
    padding: '1.5rem',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    borderLeft: `4px solid ${color}`,
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div>
        <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>{title}</p>
        <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>{value}</h2>
        {subtitle && <p style={{ margin: 0, color: '#999', fontSize: '0.75rem' }}>{subtitle}</p>}
      </div>
      <span style={{ fontSize: '2rem' }}>{icon}</span>
    </div>
  </div>
);

const OverviewTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [totalUsers, setTotalUsers] = useState(0);
  const [activeSubscriptions, setActiveSubscriptions] = useState(0);
  const [mrr, setMrr] = useState(0);
  const [totalEvents, setTotalEvents] = useState(0);
  const [activeSessionsCount, setActiveSessionsCount] = useState<number | null>(null);
  const [crashes24h, setCrashes24h] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      try {
        // Get total users
        const users = await getCollection('users', undefined, undefined, undefined, 10000);
        if (isMounted) {
          setTotalUsers(users.length);
        }

        // Get subscription metrics (may fail if Stripe is unavailable, but that's OK)
        try {
          const metrics = await aggregateSubscriptions();
          if (isMounted) {
            setActiveSubscriptions(metrics.activeSubscriptions);
            setMrr(metrics.totalMRR);
          }
        } catch (subscriptionError) {
          console.warn('Could not load subscription metrics:', subscriptionError);
          // Set defaults so dashboard still loads
          if (isMounted) {
            setActiveSubscriptions(0);
            setMrr(0);
          }
        }

        // Get total analytics events (last 30 days)
        try {
          const events = await getAnalyticsEvents(30);
          if (isMounted) {
            setTotalEvents(events.length);
          }
        } catch (eventsError) {
          console.warn('Could not load analytics events:', eventsError);
          if (isMounted) {
            setTotalEvents(0);
          }
        }

        // Active sessions (single-session-per-user; web + iOS + Android)
        try {
          const sessions = await getActiveSessions();
          if (isMounted) setActiveSessionsCount(sessions.totalActiveSessions);
        } catch {
          if (isMounted) setActiveSessionsCount(null);
        }

        // Crashes last 24h (app_crashes from Flutter)
        try {
          const crash = await getCrashAnalytics(7);
          if (isMounted) setCrashes24h(crash.totalLast24h);
        } catch {
          if (isMounted) setCrashes24h(null);
        }
      } catch (error) {
        console.error('Error loading overview data:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();
    
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '1rem' : '2rem' }}>
      <h1 style={{ 
        marginBottom: '2rem', 
        fontSize: isMobile ? '1.25rem' : '1.75rem', 
        fontWeight: 'bold' 
      }}>
        Overview
      </h1>

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile 
          ? '1fr' 
          : 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <KPICard
          title="Total Users"
          value={totalUsers}
          subtitle="All registered users"
          icon="👥"
          color="#2563eb"
        />
        <KPICard
          title="Active Subscriptions"
          value={activeSubscriptions}
          subtitle="Currently active"
          icon="💳"
          color="#10b981"
        />
        <KPICard
          title="Monthly Recurring Revenue"
          value={`$${mrr.toFixed(2)}`}
          subtitle="MRR"
          icon="💰"
          color="#f59e0b"
        />
        <KPICard
          title="Analytics Events"
          value={totalEvents.toLocaleString()}
          subtitle="Last 30 days (enhanced + standard)"
          icon="📊"
          color="#8b5cf6"
        />
        {activeSessionsCount !== null && (
          <KPICard
            title="Active Sessions"
            value={activeSessionsCount}
            subtitle="Currently logged in (1 per user)"
            icon="📱"
            color="#0ea5e9"
          />
        )}
        {crashes24h !== null && (
          <KPICard
            title="Crashes (24h)"
            value={crashes24h}
            subtitle={crashes24h > 0 ? '⚠️ See System Health' : 'None'}
            icon="🛡️"
            color={crashes24h > 0 ? '#f59e0b' : '#10b981'}
          />
        )}
      </div>

      <div style={{
        backgroundColor: 'white',
        padding: '1.5rem',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }}>
        <h2 style={{ marginTop: 0, marginBottom: '1rem' }}>Quick Insights</h2>
        <p style={{ color: '#666' }}>
          Dashboard provides real-time insights into user engagement, revenue, and system health.
          Use the tabs above to explore detailed analytics.
        </p>
      </div>
    </div>
  );
};

export default OverviewTab;

